import React from 'react'
import HelpPane from 'components/Common/HelpPane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import moment from 'moment'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { intlFormatter } from 'lib/datetime/dateUtils'
import Group from 'components/utils/Group'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'

const ModalTitles: Record<string, string> = {
  bnbl: 'Buy your escape now. Decide when to go later.',
  bestPrice: 'Our best price guarantee',
  gifting: 'Gifting available',
}
interface Props {
  open: boolean;
  onClose: () => void;
  modalType?: 'bnbl' | 'bestPrice' | 'gifting';
  bookBy: moment.Moment;
  travelUntil: moment.Moment;
}

function USPModal(props: Props) {
  const { open, onClose, modalType = '', bookBy, travelUntil } = props

  const isBestPrice = modalType === 'bestPrice'
  const isWePriceMatchEnabled = useOptimizelyExperiment(OptimizelyExperiments.croWePriceMatchOfferPageTag, isBestPrice)
  const title = isBestPrice && isWePriceMatchEnabled ? 'We price match' : ModalTitles[modalType]

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={title}
    >
      {modalType === 'bnbl' && <VerticalSpacer gap={24}>
        <BodyText variant="medium">
          Our deals are live for a limited time only, so when you see your dream escape at an unbelievable price, don't miss out because you don't know when you want to travel. Lock in your escape today, and choose your dates when you're ready.
        </BodyText>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            1. Secure your escape
          </BodyText>
          <BodyText variant="medium">
            Purchase your escape now to lock in the great price.
          </BodyText>
          <BodyText weight="bold" variant="medium">
            2. Decide on your dates
          </BodyText>
          <BodyText variant="medium">
            Take your time to choose the travel dates that suit you.
          </BodyText>
          <BodyText weight="bold" variant="medium">
            3. Book when you're ready
          </BodyText>
          <BodyText variant="medium">
            Simply log into your account and select the relevant escape.
          </BodyText>
        </Group>
        <BodyText variant="medium" italics>
          <b>Please note:</b> Not all offers can be booked as a 'Buy Now, Choose Dates Later' package.
        </BodyText>
      </VerticalSpacer>}
      {modalType === 'bestPrice' && <VerticalSpacer gap={24}>
        <BodyText variant="medium">
          We negotiate the best prices so you don't have to. In the unlikely event that you find a better price for the same offer, we promise to match it.
        </BodyText>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            1. Compare the same offer
          </BodyText>
          <BodyText variant="medium">
            Make sure the offer you've found is for the same hotel with the same VIP inclusions as our package.
          </BodyText>
          <BodyText weight="bold" variant="medium">
            2. Record the better price
          </BodyText>
          <BodyText variant="medium">
            Take a website screenshot or a photo of a catalogue so you can send it to us.
          </BodyText>
          <BodyText weight="bold" variant="medium">
            3. We'll match the price
          </BodyText>
          <BodyText variant="medium">
            Once verified we'll match the price.
          </BodyText>
        </Group>
      </VerticalSpacer>}
      {modalType === 'gifting' && <VerticalSpacer gap={24}>
        <BodyText variant="medium">
          Buy a package and send as a gift to a special someone. They must book by {intlFormatter(bookBy.toDate(), INTL_DMY_CASUAL_FORMAT)} and can travel until {intlFormatter(travelUntil.toDate(), INTL_DMY_CASUAL_FORMAT)}.
        </BodyText>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            1. Purchase online
          </BodyText>
          <BodyText variant="medium">
            Purchasing this as a gift will secure the offer for your recipient and give them the flexibility to choose travel dates at a later time. Dates must be within the book by and validity period, and are subject to availability and surcharges.
          </BodyText>
        </Group>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            2. Send or schedule your gift
          </BodyText>
          <BodyText variant="medium">
            Provide the recipient's details and schedule a date so we can email it to them on your behalf.
          </BodyText>
        </Group>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            3. The world is their oyster
          </BodyText>
          <BodyText variant="medium">
            Your recipient can manage and make changes to their booking according to our 7-day ‘change of mind’ guarantee after booking (if applicable – refer to individual offer for more information).
          </BodyText>
        </Group>
      </VerticalSpacer>}
      <HelpPane />
    </Modal>
  )
}

export default USPModal
